import { gql } from '@apollo/client';
import { useAuthContext } from '@authentication/Authentication';
import { TransitionScreen } from '@components/transition';
import {
  GetWorkspaceContextDataQuery,
  useGetWorkspaceContextDataQuery,
  useGetWorkspacesQuery,
} from '@generated/graphql';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { PropsWithChildren, createContext, useContext, useEffect, useMemo } from 'react';
import { useCookie } from 'react-use';
import { NonexistentWorkspacePage } from './NonexistentWorkspacePage';

gql`
  query GetWorkspaceContextData($workspaceId: String) {
    getWorkspaces(workspaceId: $workspaceId) {
      description
      name
      organizationId
      status
      tags
      workspaceId
      createdBy
    }
  }
`;

type WorkspaceContextType = {
  active: boolean;
  workspace: GetWorkspaceContextDataQuery['getWorkspaces'][number];
  isOwner: boolean;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
  refetch: () => Promise<unknown>;
};

const WorkspaceContext = createContext<WorkspaceContextType | null>({
  active: false,
  workspace: null,
  isOwner: false,
  startPolling: () => {},
  stopPolling: () => {},
  refetch: async () => {},
});

export const WorkspaceContextProvider = ({
  workspaceId,
  children,
}: PropsWithChildren<{ workspaceId: string }>) => {
  const { organizationId, organization } = useOrganizationContext();
  const { user } = useAuthContext();

  // Store last workspace per organization as a JSON object: { [orgId]: workspaceId }
  const [lastWorkspaceMap, setLastWorkspaceMap] = useCookie('lastWorkspaceByOrg');

  // Parse the stored workspace map
  const workspaceMap = useMemo(() => {
    try {
      return lastWorkspaceMap ? (JSON.parse(lastWorkspaceMap) as Record<string, string>) : {};
    } catch {
      return {};
    }
  }, [lastWorkspaceMap]);

  // Query for first workspace when we need a fallback
  const shouldFetchFirstWorkspace =
    !workspaceId && organizationId && !organization?.serviceOnly && !workspaceMap[organizationId];

  const firstWorkspaceQuery = useGetWorkspacesQuery({
    variables: { organizationId, limit: 1 },
    skip: !shouldFetchFirstWorkspace,
  });

  // Determine the active workspace ID
  const activeWorkspaceId = useMemo(() => {
    // If workspaceId is provided from navigation, use it
    if (workspaceId) {
      return workspaceId;
    }
    // For non-serviceOnly orgs, retrieve the last workspace for this org
    if (organizationId && !organization?.serviceOnly) {
      const cachedWorkspaceId = workspaceMap[organizationId];
      if (cachedWorkspaceId) {
        return cachedWorkspaceId;
      }
      // Fall back to first available workspace
      return firstWorkspaceQuery.data?.getWorkspaces?.[0]?.workspaceId;
    }
    return undefined;
  }, [
    workspaceId,
    organizationId,
    organization?.serviceOnly,
    workspaceMap,
    firstWorkspaceQuery.data,
  ]);

  const workspaceContextQuery = useGetWorkspaceContextDataQuery({
    variables: { workspaceId: activeWorkspaceId },
    skip: !activeWorkspaceId,
  });

  const workspace = workspaceContextQuery.data?.getWorkspaces?.[0];
  const lookupResolved = Boolean(activeWorkspaceId) && !workspaceContextQuery.loading;
  const isWorkspaceInvalid = lookupResolved && !workspace;

  // Sync the cookie with the validated workspace. Persisting `activeWorkspaceId`
  // before validation would let a bad URL workspaceId leak into the cookie and
  // softlock subsequent visits to `/{orgId}`.
  useEffect(() => {
    if (!organizationId || organization?.serviceOnly) return;

    const cachedId = workspaceMap[organizationId];

    if (workspace && cachedId !== workspace.workspaceId) {
      setLastWorkspaceMap(
        JSON.stringify({ ...workspaceMap, [organizationId]: workspace.workspaceId }),
      );
      return;
    }

    // The active workspace lookup failed and the cookie was the source of that
    // bad ID — clear it via the React setter so `workspaceMap` state stays in
    // sync (writing through `document.cookie` directly does not).
    if (isWorkspaceInvalid && cachedId && cachedId === activeWorkspaceId) {
      const updatedMap = { ...workspaceMap };
      delete updatedMap[organizationId];
      setLastWorkspaceMap(JSON.stringify(updatedMap));
    }
  }, [
    organizationId,
    organization?.serviceOnly,
    workspace,
    isWorkspaceInvalid,
    activeWorkspaceId,
    workspaceMap,
    setLastWorkspaceMap,
  ]);

  // Clear workspace for this org when switching to a serviceOnly org
  useEffect(() => {
    if (organizationId && organization?.serviceOnly && workspaceMap[organizationId]) {
      const updatedMap = { ...workspaceMap };
      delete updatedMap[organizationId];
      setLastWorkspaceMap(JSON.stringify(updatedMap));
    }
  }, [organizationId, organization?.serviceOnly, workspaceMap, setLastWorkspaceMap]);

  const value = useMemo(() => {
    return {
      active: Boolean(workspaceId),
      startPolling: workspaceContextQuery.startPolling,
      stopPolling: workspaceContextQuery.stopPolling,
      refetch: workspaceContextQuery.refetch,
      workspace,
      isOwner: workspace?.createdBy === user?.email,
    };
  }, [
    workspaceId,
    workspaceContextQuery.startPolling,
    workspaceContextQuery.stopPolling,
    workspaceContextQuery.refetch,
    workspace,
    user?.email,
  ]);

  if (activeWorkspaceId && workspaceContextQuery.loading) {
    return <TransitionScreen />;
  }

  if (isWorkspaceInvalid) {
    return <NonexistentWorkspacePage />;
  }

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useCurrentWorkspace = () => useContext(WorkspaceContext);
export const useCurrentWorkspaceId = () => {
  const { workspace } = useCurrentWorkspace();
  return workspace?.workspaceId;
};
export const useCurrentOrganizationId = () => {
  const { workspace } = useCurrentWorkspace();
  return workspace?.organizationId;
};

export const useWorkspaceContext = () => {
  const { workspace, isOwner, active } = useContext(WorkspaceContext);
  return { workspaceId: workspace?.workspaceId, workspace, isOwner, active };
};
